import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, ApiError } from "../../shared/api/client";
import type {
  PageResponse,
  ReleaseSummary,
  WorkItemDetail,
} from "../../shared/types/api";
import { Button, Drawer, InlineAlert } from "../../shared/ui";
import {
  ReleasePicker,
  releaseChoiceReady,
  type ReleaseChoice,
} from "../releases/ReleasePicker";

/**
 * Moving a work item between releases, or taking it out (§10, §14).
 *
 * <p>Its own drawer rather than a field on the edit form, because this is a scope change on one
 * or two releases rather than an edit to the item. Moving from a frozen 2.1 to 2.2 has to leave
 * a record on both — folding it into "save item" would make a governed change look like renaming
 * something.
 */
export function SetReleaseDrawer({
  item,
  open,
  onClose,
  onSaved,
}: {
  item: WorkItemDetail;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [choice, setChoice] = useState<ReleaseChoice>({
    releaseId: null,
    scopeChangeReason: null,
    scopeChangeComment: null,
  });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const releases = useQuery({
    queryKey: ["releases", "picker", item.projectId],
    queryFn: () =>
      api.get<PageResponse<ReleaseSummary>>(
        `/api/v1/projects/${item.projectId}/releases?size=200`,
      ),
    enabled: open,
  });

  useEffect(() => {
    if (!open) {
      return;
    }
    setChoice({
      releaseId: item.releaseId ?? null,
      scopeChangeReason: null,
      scopeChangeComment: null,
    });
    setError(null);
  }, [open, item]);

  const current = item.releaseId ?? null;
  const ready = releaseChoiceReady(choice, current, releases.data?.content ?? []);
  const unchanged = choice.releaseId === current;

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.put(`/api/v1/work-items/${item.id}/release`, {
        releaseId: choice.releaseId,
        scopeChangeReason: choice.scopeChangeReason,
        scopeChangeComment: choice.scopeChangeComment,
      });
      onSaved();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not change the release.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Which release carries this?"
      subtitle={`${item.itemKey} — ${item.title}`}
      footer={
        <div className="flex justify-between gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={!ready || unchanged} loading={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        <ReleasePicker
          projectId={item.projectId}
          value={choice}
          currentReleaseId={current}
          onChange={setChoice}
          hint="One release ships an item. Moving it out of another is recorded on that release too."
        />
      </div>
    </Drawer>
  );
}
